import type React from "react";
import { Center, Loader, Stack, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import type { JobViewModel } from "@models/job";
import JobsTable from "@features/MappingService/JobsTable";

interface JobListSectionProps {
  jobs: JobViewModel[];
  isLoadingInitial: boolean;
  loadedOnce: boolean;
  filterActive: boolean;
  currentUserId: string | null;
  isLoadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onOpenJob: (jobId: string) => void;
  onDeleteJob: (jobId: string) => Promise<boolean>;
}

/** Renders the job list area: initial loader, empty state, or the table. */
const JobListSection: React.FC<JobListSectionProps> = ({
  jobs,
  isLoadingInitial,
  loadedOnce,
  filterActive,
  currentUserId,
  isLoadingMore,
  hasMore,
  onLoadMore,
  onOpenJob,
  onDeleteJob,
}) => {
  const { t } = useTranslation();

  if (isLoadingInitial && jobs.length === 0) {
    return (
      <Center mih={200}>
        <Stack align="center" gap="xs">
          <Loader size="md" />
          <Text size="sm" c="dimmed">
            {t("mappingServicePage.jobs.loading")}
          </Text>
        </Stack>
      </Center>
    );
  }

  if (jobs.length === 0) {
    return (
      <Center mih={120} className="mapping-service-page__empty">
        <Stack align="center" gap="xs">
          <Text size="md" c="dimmed">
            {loadedOnce
              ? filterActive
                ? t("mappingServicePage.jobs.emptyFiltered")
                : t("mappingServicePage.jobs.empty")
              : t("mappingServicePage.jobs.loading")}
          </Text>
          <Text size="xs" c="dimmed">
            {t("mappingServicePage.jobs.emptyHint")}
          </Text>
        </Stack>
      </Center>
    );
  }

  return (
    <JobsTable
      jobs={jobs}
      currentUserId={currentUserId}
      onDeleteJob={onDeleteJob}
      isLoadingMore={isLoadingMore}
      hasMore={hasMore}
      onLoadMore={onLoadMore}
      onOpenJob={onOpenJob}
    />
  );
};

export default JobListSection;
